/**
 * Copyright Mántum All Rights Reserved.
 */
import Date from '../date';
import Time from '../time';
import { formatter } from '@mantum/kanela';
import { forwardRef, useRef, useImperativeHandle, useCallback } from 'react';

import type {
    Constraint,
    Detail,
    FormatDateTime as Format,
    Locale,
    Validation
} from '@mantum/kanela';

const KEY_TEMPLATE_DATE = 'YYYY-MM-DD';
const KEY_TEMPLATE_TIME = 'HH:mm:ss';

export type Placeholder = {
    date?: string;
    time?: string;
};

type PropTypes = {
    advanced?: {
        locale?: Locale;
    };
    disabled?: boolean;
    error?: boolean;
    format?: Format;
    name?: string;
    onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
    onChange?: (name?: string, value?: unknown) => void;
    placeholder?: Placeholder;
    optional?: boolean;
    testid?: string;
    validation?: {
        date?: Constraint;
        time?: Constraint;
    } & Validation;
    value?: string | Date | 'now' | number;
};

export default forwardRef<Detail<unknown>, PropTypes>(
    function DateTime(props, ref) {
        // prettier-ignore
        const {
        format = [],
        name
    } = props

        const [formatDate, formatTime] = format;

        const dateRef = useRef<Detail<unknown>>(null);
        const timeRef = useRef<Detail<unknown>>(null);
        const valueRef = useRef<Record<string, unknown>>({});

        const getDateName = useCallback(() => {
            return `${name}-date`;
        }, [name]);

        const getTimeName = useCallback(() => {
            return `${name}-time`;
        }, [name]);

        const getValue = useCallback(() => {
            if (getDateName() in valueRef.current) {
                const date = valueRef.current[getDateName()];
                const time = valueRef.current[getTimeName()];

                const from = formatter.date.from(`${date} ${time}`);
                if (from) {
                    return formatter.datetime.format(from, [
                        KEY_TEMPLATE_DATE,
                        KEY_TEMPLATE_TIME
                    ]);
                }
            }

            return '';
        }, [getDateName, getTimeName]);

        useImperativeHandle(
            ref,
            () => {
                return {
                    name: name,
                    validable: true,
                    clear() {
                        dateRef.current?.clear();
                        timeRef.current?.clear();
                    },
                    validate() {
                        const dateResult = dateRef.current?.validate();
                        const timeResult = timeRef.current?.validate();

                        if (dateResult && timeResult) {
                            return true;
                        }
                        return false;
                    },
                    value() {
                        return getValue();
                    }
                };
            },
            [name, getValue]
        );

        const onChange = (name?: string, value?: unknown) => {
            if (props.onChange) {
                valueRef.current = {
                    ...valueRef.current,
                    [name as keyof unknown]: value
                };
                props.onChange(props.name, getValue());
            }
        };

        return (
            <div className='flex w-full gap-2'>
                <div className='basis-1/2 md:basis-2/3'>
                    <Date
                        {...props}
                        format={formatDate}
                        name={props.name ? getDateName() : ''}
                        onChange={onChange}
                        ref={dateRef}
                        placeholder={props.placeholder?.date}
                        validation={{
                            date: props.validation?.date,
                            message: props.validation?.message ?? ''
                        }}
                        testid={props.testid ? `${props.testid}-date` : ''}
                    />
                </div>
                <div className='basis-1/2 md:basis-2/6'>
                    <Time
                        {...props}
                        format={formatTime}
                        name={props.name ? getTimeName() : ''}
                        onChange={onChange}
                        ref={timeRef}
                        placeholder={props.placeholder?.time}
                        validation={{
                            date: props.validation?.time,
                            message: props.validation?.message ?? ''
                        }}
                        testid={props.testid ? `${props.testid}-time` : ''}
                    />
                </div>
            </div>
        );
    }
);
